import axios from 'axios';
import { LoginCredentials, RegisterData, ParentRegisterData, TeacherRegisterData, User, AuthResponse } from '../types/auth';
import apiClient from './apiClient';  // Use the shared apiClient instance
import { setToken, removeToken, isTokenValid } from '../utils/jwtUtils';

// Shared apiClient is already configured with interceptors

// Test API connectivity
export const testApiConnection = async (): Promise<boolean> => {
  try {
    await apiClient.get('/api/health');
    return true;
  } catch (error) {
    console.error('API Connection test failed:', error);
    // Try one more time with debug endpoint
    try {
      await apiClient.get('/api/debug/health');
      return true;
    } catch (fallbackError) {
      console.error('Fallback API connection test failed:', fallbackError);
      return false;
    }
  }
};


// Login functionality
export const loginUser = async (credentials: LoginCredentials): Promise<{ user: User }> => {
  try {
    // JWT auth - receive token in response
    const response = await apiClient.post('/api/auth/login', credentials);

    // Store token in localStorage using our utility
    if (response.data.token) {
      setToken(response.data.token, response.data.expiresIn || 86400);
    }

    // Return user data
    return { user: response.data.user };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message || 'Login failed');
    }
    if (error instanceof Error) {
      throw error;
    }
    throw new Error('Network error. Please try again later.');
  }
};

// Register functionality
export const registerUser = async (userData: RegisterData): Promise<{ user: User }> => {
  try {
    // Remove confirmPassword as it's not needed for the API
    const { confirmPassword: _confirmPassword, ...registerData } = userData;

    const response = await apiClient.post('/api/auth/register', registerData);

    // Store token in localStorage using our utility
    if (response.data.token) {
      setToken(response.data.token, response.data.expiresIn || 86400);
    }

    // Return user data
    return { user: response.data.user };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      throw new Error(error.response.data.message || 'Registration failed');
    }
    throw new Error('Network error. Please try again later.');
  }
};

// Register parent functionality
export const registerParent = async (parentData: ParentRegisterData): Promise<{ user: User; child: User }> => {
  try {
    // Remove confirmPassword from parentData as it's not needed for the API
    const { confirmPassword: _confirmPassword, ...parentInfo } = parentData.parentData;

    // Prepare the request payload
    const requestData = {
      parentData: parentInfo,
      childOption: parentData.childOption,
      childData: parentData.childData
    };

    const response = await apiClient.post('/api/auth/register-parent', requestData);

    // Store token in localStorage using our utility
    if (response.data.token) {
      setToken(response.data.token, response.data.expiresIn || 86400);
    }

    // Return both parent and child data
    return {
      user: response.data.parent,
      child: response.data.child
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Parent registration failed';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};

// Register teacher functionality
export const registerTeacher = async (teacherData: TeacherRegisterData): Promise<{ teacher: User; student?: User; token: string }> => {
  try {
    // Remove confirmPassword from teacherData as it's not needed for the API
    const { confirmPassword: _confirmPassword, ...teacherInfo } = teacherData.teacherData;

    // Prepare the request payload
    const requestData = {
      teacherData: teacherInfo,
      linkedStudentEmail: teacherData.linkedStudentEmail
    };

    const response = await apiClient.post('/api/auth/register/teacher', requestData);

    // Store token in localStorage using our utility
    if (response.data.token) {
      setToken(response.data.token, response.data.expiresIn || 86400);
    }

    // Return teacher, optional student, and token
    return {
      teacher: response.data.teacher,
      student: response.data.student,
      token: response.data.token
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      const errorMessage =
        error.response.data.message ||
        error.response.data.error ||
        'Teacher registration failed';
      throw new Error(errorMessage);
    }
    throw new Error('Network error. Please try again later.');
  }
};


// Get current user functionality
export const getCurrentUser = async (): Promise<User> => {
  try {
    // Check if we have a valid token
    if (!isTokenValid()) {
      throw new Error('No valid authentication token found');
    }
    
    const response = await apiClient.get('/api/auth/me');
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      console.error('Get current user error:', error.response.data);
      throw new Error(error.response.data.message || 'Failed to get user information');
    }
    throw new Error('Network error. Please try again later.');
  }
};

// Logout functionality
export const logoutUser = async (): Promise<void> => {
  try {
    // Notify server of logout (optional with JWT)
    await apiClient.post('/api/auth/logout');

    // Remove JWT token from localStorage
    removeToken();
  } catch (error) {
    console.error('Logout error:', error);
    
    // Even if server-side logout fails, clear token
    removeToken();
  }
};

// Forgot password functionality
export const forgotPassword = async (email: string): Promise<void> => {
  try {
    await apiClient.post('/api/auth/forgot-password', { email });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      console.error('Forgot password error:', error.response.data);
      throw new Error(error.response.data.message || 'Failed to send password reset email');
    }
    throw new Error('Network error. Please try again later.');
  }
};

// Reset password with token functionality
export const resetPassword = async (token: string, newPassword: string): Promise<void> => {
  try {
    await apiClient.post('/api/auth/reset-password', { token, newPassword });
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      console.error('Reset password error:', error.response.data);
      throw new Error(error.response.data.message || 'Failed to reset password');
    }
    throw new Error('Network error. Please try again later.');
  }
};

/**
 * Register a student using a teacher invitation token
 */
export const registerWithInvitation = async (
  token: string,
  email: string,
  password: string,
  firstName: string,
  lastName: string,
  username: string,
  school?: string,
  grade?: number,
  parentEmail?: string
): Promise<AuthResponse> => {
  try {
    const response = await apiClient.post<AuthResponse>('/auth/register/invitation', {
      token,
      email,
      password,
      firstName,
      lastName,
      username,
      school,
      grade,
      parentEmail
    });
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error) && error.response) {
      console.error('Invitation registration error:', error.response.data);
      throw new Error(error.response.data.error || 'Failed to register with invitation');
    }
    throw new Error('Network error. Please try again later.');
  }
};

export default apiClient;